import { useState, useMemo } from "react";
import { GAME } from "@/data/gameData";
import { ArrowRight, RotateCcw, Users, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Game = () => {
  const [started, setStarted] = useState(false);
  const [nodeId, setNodeId] = useState(GAME.start);
  const [path, setPath] = useState([]);

  const node = useMemo(() => GAME.nodes[nodeId], [nodeId]);
  const isOutcome = !!node.outcome;

  const start = () => {
    setStarted(true);
    setNodeId(GAME.start);
    setPath([]);
  };

  const chooseNext = (choice) => {
    const nextPath = [...path, choice.label];
    setPath(nextPath);
    setNodeId(choice.next);

    const nextNode = GAME.nodes[choice.next];
    if (nextNode?.outcome) {
      // Record game run silently; never block UI on failure.
      axios
        .post(`${API}/game/runs`, {
          outcome: nextNode.outcome.title,
          path: nextPath,
          survivors: nextNode.outcome.survivors,
        })
        .catch(() => {});
    }
  };

  const restart = () => {
    setNodeId(GAME.start);
    setPath([]);
  };

  if (!started) {
    return (
      <div
        className="max-w-3xl mx-auto px-6 lg:px-12 py-24 lg:py-32"
        data-testid="game-intro"
      >
        <div className="overline text-[#D4AF37]">A serious game — not entertainment</div>
        <h1 className="font-serif-display text-4xl lg:text-6xl text-[#F2EBE5] mt-4 leading-tight">
          The Path of Survival
        </h1>
        <p className="text-[#A19D98] mt-8 leading-relaxed">
          You are a Yazidi parent in a village near Sinjar town. It is just
          before dawn on <span className="text-[#F2EBE5]">August 3, 2014</span>. The
          choices ahead are the same impossible choices that real families
          faced. There are no right answers — only consequences shaped by
          history.
        </p>
        <p className="text-[#A19D98] mt-4 leading-relaxed">
          This experience contains no graphic content. Every branch is grounded
          in publicly documented survivor testimony.
        </p>

        <div className="border border-white/10 p-6 mt-12 bg-[#111215]">
          <div className="overline mb-3">Before you begin</div>
          <ul className="text-sm text-[#A19D98] space-y-2 list-disc list-inside">
            <li>This story is told with respect. Please carry it with you.</li>
            <li>If you find it heavy, that is right. Take your time.</li>
            <li>When you finish, please visit the Memorial and Take Action pages.</li>
          </ul>
        </div>

        <div className="mt-12 flex gap-4 flex-wrap">
          <button
            onClick={start}
            data-testid="game-start-button"
            className="px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors duration-500 inline-flex items-center gap-2"
          >
            Begin <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <Link
            to="/timeline"
            data-testid="game-see-timeline"
            className="px-6 py-3 border border-white/20 text-[#F2EBE5] text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-500"
          >
            See the timeline first
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="max-w-2xl mx-auto px-6 lg:px-12 py-24 lg:py-32"
      data-testid="game-screen"
    >
      {!isOutcome ? (
        <>
          <div className="overline text-[#D4AF37]" data-testid="game-stage">
            {node.stage}
          </div>
          <p className="font-serif-display text-2xl lg:text-3xl text-[#F2EBE5] mt-6 leading-relaxed fade-up">
            {node.text}
          </p>

          <div className="mt-12 border-t border-white/10">
            {node.choices.map((c, i) => (
              <button
                key={i}
                onClick={() => chooseNext(c)}
                data-testid={c.testid}
                className="w-full text-left py-5 border-b border-white/10 flex items-start gap-4 group hover:bg-white/[0.03] transition-colors duration-500"
              >
                <span className="overline text-[#6E6B67] group-hover:text-[#D4AF37] pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[#F2EBE5] group-hover:text-[#D4AF37] text-base leading-relaxed transition-colors duration-500">
                  {c.label}
                </span>
                <ArrowRight className="ml-auto w-4 h-4 text-[#6E6B67] group-hover:text-[#D4AF37] mt-1 transition-colors duration-500" strokeWidth={1.5} />
              </button>
            ))}
          </div>

          <div className="mt-12 text-xs text-[#6E6B67] flex items-center gap-2">
            <Users className="w-3 h-3" strokeWidth={1.5} />
            Step {path.length + 1}
          </div>
        </>
      ) : (
        <div data-testid="game-outcome">
          <div className="overline text-[#D4AF37]">Outcome</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl text-[#F2EBE5] mt-4 leading-tight">
            {node.outcome.title}
          </h2>
          <p className="text-[#A19D98] mt-8 leading-relaxed text-lg">
            {node.outcome.body}
          </p>

          <div className="border border-white/10 mt-12 p-6 bg-[#111215]">
            <div className="overline flex items-center gap-2">
              <BookOpen className="w-3 h-3" strokeWidth={1.5} /> What history records
            </div>
            <p className="text-[#A19D98] text-sm mt-4 leading-relaxed">
              {node.outcome.historical}
            </p>
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <button
              onClick={restart}
              data-testid="game-restart-button"
              className="px-6 py-3 border border-white/20 text-[#F2EBE5] text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-500 inline-flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Walk again
            </button>
            <Link
              to="/memorial"
              data-testid="game-go-memorial"
              className="px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors duration-500 inline-flex items-center gap-2"
            >
              Light a candle <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/act"
              data-testid="game-go-act"
              className="px-6 py-3 border border-white/20 text-[#F2EBE5] text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-500"
            >
              Take action
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Game;
